import { Fragment, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getApiErrorCode, getApiErrorMessage,
  transfersAPI,
  type TransferDetailDto, type TransferLineDto, type TransferRowDto, type TransferRowWithLinesDto,
  type TransferScope,
} from '@matix/api-client';
import {
  ReportToolbar, Field, DateTimeRange, fmt3, fmtInt, fmtMoney,
  todayFrom, todayTo, rangeParams, rangeLabel,
} from './reportShared';
import { printReport, type ReportPaper } from './reportPrint';
import { ReportSheetBuilder, downloadReportExcel, type ExcelColumn, type ExcelRow } from './reportExcel';
import { wallClock } from './reportShared';
import { useLang } from '../../lib/language';
import { toast } from '../../store/toast';
import { storeName } from '../transfers/shared';

/**
 * Transfers Report — the Code 81 "Transfer Goods" documents (سندات التحويل)
 * this branch's store is an end of, and the destination's item-by-item
 * check-in of them.
 *
 * ── Why goods documents only ──────────────────────────────────────────────
 *
 * A Code 81 IS the transfer: the ERP writes it on its own (every line twice,
 * −1 at the source, +1 at the destination) and the stock has moved the moment
 * it exists. Requests (Code 80) are the Transfers workspace's business; a
 * tenant such as GL_WEB_binqinwan has none at all. So the report lists goods
 * documents, and the Type and Status filters are gone.
 *
 * ── Receiving is per ITEM ─────────────────────────────────────────────────
 *
 * Expanding a document shows its items — code, name, qty, unit, unit price,
 * total — each with a checkbox. The price is the SELLING price of the line's
 * unit (Sale_price1) and total = qty × it (asked for 2026-10-06); the transfer
 * cost is still never shown. The destination till ticks what physically arrived and
 * presses Receive (or Select all → Receive): a CHECK-IN recorded on the server
 * with who and when (pos_transfer_goods_receipt, migration 063), never a stock
 * movement. A received item stays ticked and shows by whom. A goods document
 * our own till wrote when it received a request arrived in one go, and reads
 * as received in full.
 *
 * Direction defaults to INCOMING: the counter's question is "what is coming to
 * us, and has it all arrived?".
 *
 * ── Print and Excel ───────────────────────────────────────────────────────
 *
 * Both are built from DATA, not from the screen: every document on the page
 * with ALL its items (one `include_lines` call), whether or not it is
 * expanded. A4 keeps each document as a table; the 72 mm roll turns each
 * item into a card (`fitToRoll`), under the document's own heading.
 */

interface Params {
  scope: TransferScope;
  q?: string;
  from: string;
  to: string;
  from_time?: string;
  to_time?: string;
  page: number;
  limit: number;
}

const PAGE_SIZE = 100;

type Tr = (m: { en: string; ar: string }) => string;

export function TransfersReport(): JSX.Element {
  const { t, lang } = useLang();
  const qc = useQueryClient();

  const [scope, setScope] = useState<TransferScope>('incoming');
  const [term, setTerm] = useState('');
  const [from, setFrom] = useState(todayFrom);
  const [to, setTo] = useState(todayTo);
  const [params, setParams] = useState<Params | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [exporting, setExporting] = useState(false);

  const listQuery = (p: Params) => ({ kind: 'goods' as const, status: 'all' as const, ...p });

  const dataQ = useQuery({
    queryKey: ['transfers-report', params],
    queryFn: () => transfersAPI.list(listQuery(params!)),
    enabled: params != null,
  });

  const range = rangeLabel(from, to);
  const title = t({ en: 'Transfer Goods Report', ar: 'تقرير سندات التحويل' });
  const scopeLabel = {
    incoming: t({ en: 'Incoming to my store', ar: 'وارد إلى مخزني' }),
    outgoing: t({ en: 'Outgoing from my store', ar: 'صادر من مخزني' }),
    all: t({ en: 'Incoming & outgoing', ar: 'وارد وصادر' }),
  }[scope];
  const subtitle = `${scopeLabel} · ${range}`;

  function run(page = 1): void {
    const r = rangeParams(from, to);
    if (!r) return;
    setExpanded(new Set());
    setParams({ scope, ...r, page, limit: PAGE_SIZE, ...(term.trim() ? { q: term.trim() } : {}) });
  }

  function toggle(id: number): void {
    setExpanded((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const d = dataQ.data;
  const rows = d?.data ?? [];
  const labels = useMemo(() => reportLabels(t), [t]);

  /** Every document on the page with all its lines — what print and Excel use. */
  async function loadWithLines(): Promise<TransferRowWithLinesDto[]> {
    if (!params) return [];
    const res = await qc.fetchQuery({
      queryKey: ['transfers-report', params, 'lines'],
      queryFn: () => transfersAPI.list({ ...listQuery(params), include_lines: '1' }),
      staleTime: 0,
    });
    return res.data;
  }

  async function exportPdf(paper: ReportPaper): Promise<void> {
    setExporting(true);
    try {
      const docs = await loadWithLines();
      printReport(title, subtitle, buildGoodsPrintHtml(docs, labels, lang, subtitle), paper);
    } catch (err) {
      toast(getApiErrorMessage(err), 'error');
    } finally {
      setExporting(false);
    }
  }

  async function exportExcel(): Promise<void> {
    setExporting(true);
    try {
      const docs = await loadWithLines();
      const columns: ExcelColumn[] = [
        { label: labels.docNo, kind: 'center' },
        { label: labels.date, kind: 'center' },
        { label: labels.fromStore },
        { label: labels.toStore },
        { label: labels.code, kind: 'center' },
        { label: labels.item },
        { label: labels.qty, kind: 'qty' },
        { label: labels.unit, kind: 'center' },
        { label: labels.unitPrice, kind: 'money' },
        { label: labels.lineTotal, kind: 'money' },
        { label: labels.received, kind: 'center' },
        { label: labels.receivedBy },
        { label: labels.receivedAt, kind: 'center' },
      ];
      const body: ExcelRow[] = [];
      let qty = 0;
      let value = 0;
      let lineCount = 0;
      let receivedCount = 0;
      for (const doc of docs) {
        for (const l of doc.lines ?? []) {
          lineCount++;
          if (l.received) receivedCount++;
          qty += l.qty;
          value += l.sale_total ?? 0;
          body.push([
            doc.entry_no,
            wallClock(doc.created_at),
            storeName(doc.from_store, lang),
            storeName(doc.to_store, lang),
            l.code,
            lineName(l, lang),
            l.qty,
            lineUnit(l, lang),
            l.sale_price ?? 0,
            l.sale_total ?? 0,
            receivedText(doc, l, labels),
            l.received?.by_name ?? '',
            wallClock(l.received?.at ?? null),
          ]);
        }
      }
      const sheet = new ReportSheetBuilder()
        .title(title, subtitle)
        .table(columns, body, {
          emptyText: labels.empty,
          ...(body.length > 0
            ? {
                totals: [
                  `${labels.total} (${fmtInt(docs.length)} ${labels.docs} · ${fmtInt(lineCount)} ${labels.items})`,
                  null, null, null, null, null,
                  qty, null, null, value,
                  `${fmtInt(receivedCount)} / ${fmtInt(lineCount)}`,
                  null, null,
                ],
              }
            : {}),
        })
        .build(title, { rightToLeft: lang === 'ar' });
      downloadReportExcel(`${title} ${range}`, sheet);
    } catch (err) {
      toast(getApiErrorMessage(err), 'error');
    } finally {
      setExporting(false);
    }
  }

  const totals = rows.reduce(
    (s, r) => ({
      items: s.items + r.line_count,
      qty: s.qty + r.total_qty,
      value: s.value + (r.sale_value ?? 0),
      received: s.received + (r.received_lines ?? 0),
    }),
    { items: 0, qty: 0, value: 0, received: 0 },
  );
  const pageCount = d ? Math.max(1, Math.ceil(d.total / d.limit)) : 1;
  const COLS = 11;

  return (
    <div className="rpt-wrap">
      <ReportToolbar
        running={dataQ.isFetching || exporting}
        canExport={!!d && rows.length > 0 && !exporting}
        onRun={() => run(1)}
        onExport={(paper) => void exportPdf(paper)}
        onExportExcel={() => void exportExcel()}
        filters={
          <>
            <Field label={t({ en: 'Direction', ar: 'الاتجاه' })}>
              <select value={scope} onChange={(e) => setScope(e.target.value as TransferScope)}>
                <option value="incoming">{t({ en: 'Incoming (to my store)', ar: 'وارد (إلى مخزني)' })}</option>
                <option value="outgoing">{t({ en: 'Outgoing (from my store)', ar: 'صادر (من مخزني)' })}</option>
                <option value="all">{t({ en: 'Incoming & outgoing', ar: 'وارد وصادر' })}</option>
              </select>
            </Field>
            <Field label={t({ en: 'Search', ar: 'بحث' })}>
              <input
                type="text"
                value={term}
                placeholder={t({ en: 'No. or reference', ar: 'الرقم أو المرجع' })}
                onChange={(e) => setTerm(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') run(1); }}
              />
            </Field>
            <DateTimeRange from={from} to={to} onFrom={setFrom} onTo={setTo} />
          </>
        }
      />

      {dataQ.error && (
        <div className="rpt-error">
          {t({ en: 'Failed to load report. Try again.', ar: 'تعذّر تحميل التقرير. حاول مجدداً.' })}
        </div>
      )}

      <div className="rpt-print">
        {d && (
          <>
            <div className="rpt-meta">{subtitle}</div>
            {rows.length === 0 ? (
              <div className="rpt-empty">{labels.empty}</div>
            ) : (
              <div className="rpt-scroll">
                <table className="rpt-wide tg-table">
                  <thead>
                    <tr>
                      <th>{labels.docNo}</th>
                      <th>{labels.date}</th>
                      <th>{labels.direction}</th>
                      <th>{labels.fromStore}</th>
                      <th>{labels.toStore}</th>
                      <th className="num">{labels.items}</th>
                      <th className="num">{labels.totalQty}</th>
                      <th className="num">{labels.totalValue}</th>
                      <th>{labels.received}</th>
                      <th>{labels.createdBy}</th>
                      <th>{labels.reference}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const isOpen = expanded.has(r.id);
                      return (
                        <Fragment key={r.id}>
                          <tr
                            className={`tr-rpt-row ${isOpen ? 'is-open' : ''}`}
                            onClick={() => toggle(r.id)}
                            aria-expanded={isOpen}
                          >
                            <td>
                              <span className="tr-rpt-caret">{isOpen ? '▾' : '▸'}</span>#{r.entry_no}
                            </td>
                            <td>{wallClock(r.created_at)}</td>
                            <td>{directionText(r, labels)}</td>
                            <td>{storeName(r.from_store, lang)}</td>
                            <td>{storeName(r.to_store, lang)}</td>
                            <td className="num">{fmtInt(r.line_count)}</td>
                            <td className="num">{fmt3(r.total_qty)}</td>
                            <td className="num">{fmtMoney(r.sale_value)}</td>
                            <td><ReceiptChip row={r} labels={labels} /></td>
                            <td>{r.created_by_name ?? ''}</td>
                            <td>{r.reference_no ?? ''}</td>
                          </tr>
                          {isOpen && (
                            <tr className="tg-detail">
                              <td colSpan={COLS}>
                                <GoodsItems row={r} labels={labels} />
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={5}>
                        {labels.total} ({fmtInt(rows.length)} {labels.docs})
                      </td>
                      <td className="num">{fmtInt(totals.items)}</td>
                      <td className="num">{fmt3(totals.qty)}</td>
                      <td className="num">{fmtMoney(totals.value)}</td>
                      <td>{fmtInt(totals.received)} / {fmtInt(totals.items)}</td>
                      <td colSpan={2} />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
            {pageCount > 1 && (
              <div className="rpt-pager">
                <button
                  type="button"
                  className="rpt-btn rpt-btn-sm"
                  disabled={d.page <= 1 || dataQ.isFetching}
                  onClick={() => run(d.page - 1)}
                >
                  {t({ en: 'Previous', ar: 'السابق' })}
                </button>
                <span className="rpt-pager-info">
                  {t({
                    en: `Page ${fmtInt(d.page)} of ${fmtInt(pageCount)} · ${fmtInt(d.total)} documents`,
                    ar: `صفحة ${fmtInt(d.page)} من ${fmtInt(pageCount)} · ${fmtInt(d.total)} سند`,
                  })}
                </span>
                <button
                  type="button"
                  className="rpt-btn rpt-btn-sm"
                  disabled={d.page >= pageCount || dataQ.isFetching}
                  onClick={() => run(d.page + 1)}
                >
                  {t({ en: 'Next', ar: 'التالي' })}
                </button>
              </div>
            )}
          </>
        )}
        {!d && !dataQ.isFetching && (
          <div className="rpt-empty">
            {t({ en: 'Choose filters, then “Show report”.', ar: 'اختر المرشحات، ثم “عرض التقرير”.' })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Labels ─────────────────────────────────────────────────────────────────

export type GoodsLabels = ReturnType<typeof reportLabels>;

function reportLabels(t: Tr) {
  return {
    docNo: t({ en: 'Doc. No.', ar: 'رقم السند' }),
    date: t({ en: 'Date', ar: 'التاريخ' }),
    direction: t({ en: 'Direction', ar: 'الاتجاه' }),
    incoming: t({ en: 'Incoming', ar: 'وارد' }),
    outgoing: t({ en: 'Outgoing', ar: 'صادر' }),
    other: t({ en: 'Other', ar: 'أخرى' }),
    fromStore: t({ en: 'From store', ar: 'من مخزن' }),
    toStore: t({ en: 'To store', ar: 'إلى مخزن' }),
    items: t({ en: 'Items', ar: 'الأصناف' }),
    totalQty: t({ en: 'Total qty', ar: 'إجمالي الكمية' }),
    received: t({ en: 'Received', ar: 'الاستلام' }),
    createdBy: t({ en: 'Created by', ar: 'أنشأه' }),
    reference: t({ en: 'Reference', ar: 'المرجع' }),
    code: t({ en: 'Code', ar: 'الكود' }),
    item: t({ en: 'Item', ar: 'الصنف' }),
    qty: t({ en: 'Qty', ar: 'الكمية' }),
    unit: t({ en: 'Unit', ar: 'الوحدة' }),
    unitPrice: t({ en: 'Unit price', ar: 'سعر الوحدة' }),
    lineTotal: t({ en: 'Total', ar: 'الإجمالي' }),
    totalValue: t({ en: 'Total value', ar: 'إجمالي القيمة' }),
    total: t({ en: 'Total', ar: 'الإجمالي' }),
    receivedBy: t({ en: 'Received by', ar: 'استلمه' }),
    receivedAt: t({ en: 'Received at', ar: 'وقت الاستلام' }),
    docs: t({ en: 'documents', ar: 'سند' }),
    empty: t({ en: 'No transfer goods documents for these filters.', ar: 'لا توجد سندات تحويل لهذه المرشحات.' }),
    allReceived: t({ en: 'Received', ar: 'تم الاستلام' }),
    noneReceived: t({ en: 'Not received', ar: 'لم يُستلم' }),
    partReceived: (n: number, of: number) =>
      t({ en: `${n} of ${of} received`, ar: `استُلم ${n} من ${of}` }),
    pending: t({ en: 'Pending', ar: 'بانتظار الاستلام' }),
    cancelled: t({ en: 'Cancelled', ar: 'ملغي' }),
    byWhom: (name: string) => t({ en: `by ${name}`, ar: `بواسطة ${name}` }),
    atOnce: t({ en: 'with the request', ar: 'مع الطلب' }),
  };
}

function directionText(r: TransferRowDto, l: GoodsLabels): string {
  return r.direction === 'incoming' ? l.incoming : r.direction === 'outgoing' ? l.outgoing : l.other;
}

function lineName(l: TransferLineDto, lang: string): string {
  return (lang === 'ar' ? l.name_ar : l.name) || l.name || l.name_ar || '';
}

function lineUnit(l: TransferLineDto, lang: string): string {
  return (lang === 'ar' ? l.unit_name_ar : l.unit_name) || l.unit_name || l.unit_name_ar || '';
}

/** A document's check-in progress in words. */
export function receiptText(r: TransferRowDto, l: GoodsLabels): string {
  if (r.status === 'cancelled') return l.cancelled;
  const n = r.received_lines ?? 0;
  if (r.line_count > 0 && n >= r.line_count) return l.allReceived;
  if (n === 0) return l.noneReceived;
  return l.partReceived(n, r.line_count);
}

/** One line's check-in in words. */
function receivedText(doc: TransferRowDto, line: TransferLineDto, l: GoodsLabels): string {
  if (doc.status === 'cancelled') return l.cancelled;
  return line.received ? l.allReceived : l.pending;
}

function ReceiptChip({ row, labels }: { row: TransferRowDto; labels: GoodsLabels }): JSX.Element {
  const n = row.received_lines ?? 0;
  const state =
    row.status === 'cancelled' ? 'cancelled'
      : row.line_count > 0 && n >= row.line_count ? 'all'
        : n > 0 ? 'part' : 'none';
  return <span className={`tg-chip tg-chip--${state}`}>{receiptText(row, labels)}</span>;
}

// ── The expanded document: items + check-in ────────────────────────────────

/**
 * One document's items as a real table — code, item, qty, unit, and whether it has been received — with a checkbox per item still to
 * receive, Select all in the header, and Receive for the ticked ones.
 *
 * The lines are fetched on first expand and cached; a receive writes the
 * server's answer straight back into that cache and refreshes the list row's
 * progress, so what the cashier sees is always what was saved.
 */
function GoodsItems({ row, labels }: { row: TransferRowDto; labels: GoodsLabels }): JSX.Element {
  const { t, lang } = useLang();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['transfer-detail', row.id],
    queryFn: () => transfersAPI.get(row.id),
    staleTime: 30_000,
  });
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);

  if (q.isPending) {
    return <div className="tg-note">{t({ en: 'Loading items…', ar: 'جارٍ تحميل الأصناف…' })}</div>;
  }
  if (q.error || !q.data) {
    return <div className="tg-note">{t({ en: 'Could not load the items.', ar: 'تعذّر تحميل الأصناف.' })}</div>;
  }

  const doc: TransferDetailDto = q.data;
  const canReceive = doc.can_receive_items === true;
  const open = doc.lines.filter((l) => !l.received);
  const allPicked = open.length > 0 && open.every((l) => picked.has(l.id));
  const totalQty = doc.lines.reduce((s, l) => s + l.qty, 0);
  const totalValue = doc.lines.reduce((s, l) => s + (l.sale_total ?? 0), 0);
  const receivedCount = doc.lines.length - open.length;

  function flip(id: number): void {
    setConfirming(false);
    setPicked((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function flipAll(): void {
    setConfirming(false);
    setPicked(allPicked ? new Set() : new Set(open.map((l) => l.id)));
  }

  async function receive(): Promise<void> {
    const ids = open.filter((l) => picked.has(l.id)).map((l) => l.id);
    if (ids.length === 0) return;
    setSaving(true);
    try {
      const next = await transfersAPI.receiveItems(row.id, ids);
      qc.setQueryData(['transfer-detail', row.id], next);
      void qc.invalidateQueries({ queryKey: ['transfers-report'] });
      setPicked(new Set());
      setConfirming(false);
      toast(
        t({
          en: `${ids.length} item(s) received on transfer #${row.entry_no}`,
          ar: `تم استلام ${ids.length} صنف في سند التحويل #${row.entry_no}`,
        }),
        'success',
      );
    } catch (err) {
      toast(
        getApiErrorCode(err) === 'RECEIPT_TRACKING_UNAVAILABLE'
          ? t({
              en: 'Receiving items needs a database update that this company has not had yet. Ask your administrator.',
              ar: 'استلام الأصناف يحتاج تحديثاً لقاعدة البيانات لم يُطبَّق بعد لهذه الشركة. راجع مسؤول النظام.',
            })
          : getApiErrorMessage(err),
        'error',
      );
    } finally {
      setSaving(false);
    }
  }

  const selected = open.filter((l) => picked.has(l.id)).length;

  return (
    <div className="tg-items">
      {canReceive ? (
        <div className="tg-bar">
          <span className="tg-bar-text">
            {t({
              en: `Tick the items that arrived, then Receive. ${receivedCount} of ${doc.lines.length} received so far.`,
              ar: `حدد الأصناف التي وصلت ثم اضغط استلام. تم استلام ${receivedCount} من ${doc.lines.length} حتى الآن.`,
            })}
          </span>
          {confirming ? (
            <span className="tg-bar-actions">
              <button
                type="button"
                className="rpt-btn rpt-btn-primary"
                disabled={saving}
                onClick={() => void receive()}
              >
                {saving
                  ? t({ en: 'Saving…', ar: 'جارٍ الحفظ…' })
                  : t({ en: `Confirm: ${selected} item(s) are here`, ar: `تأكيد: ${selected} صنف وصل` })}
              </button>
              <button type="button" className="rpt-btn" disabled={saving} onClick={() => setConfirming(false)}>
                {t({ en: 'Back', ar: 'رجوع' })}
              </button>
            </span>
          ) : (
            <span className="tg-bar-actions">
              <button type="button" className="rpt-btn" onClick={flipAll} disabled={open.length === 0}>
                {allPicked
                  ? t({ en: 'Clear selection', ar: 'إلغاء التحديد' })
                  : t({ en: `Select all (${open.length})`, ar: `تحديد الكل (${open.length})` })}
              </button>
              <button
                type="button"
                className="rpt-btn rpt-btn-primary"
                disabled={selected === 0}
                onClick={() => setConfirming(true)}
              >
                {t({ en: `Receive selected (${selected})`, ar: `استلام المحدد (${selected})` })}
              </button>
            </span>
          )}
        </div>
      ) : (
        <div className="tg-note">
          {doc.status === 'cancelled'
            ? t({ en: 'This document was cancelled.', ar: 'تم إلغاء هذا السند.' })
            : receivedCount === doc.lines.length && doc.lines.length > 0
              ? t({ en: 'Every item on this document has been received.', ar: 'تم استلام كل أصناف هذا السند.' })
              : doc.direction !== 'incoming'
                ? t({
                    en: 'View only — the destination store receives these items.',
                    ar: 'للعرض فقط — المخزن المستلم هو من يستلم هذه الأصناف.',
                  })
                : doc.receipt_tracking === false
                  ? t({
                      en: 'Receiving items needs a database update that this company has not had yet.',
                      ar: 'استلام الأصناف يحتاج تحديثاً لقاعدة البيانات لم يُطبَّق بعد لهذه الشركة.',
                    })
                  : ''}
        </div>
      )}

      <table className="tg-lines">
        <thead>
          <tr>
            <th className="tg-check">
              {canReceive && (
                <input
                  type="checkbox"
                  checked={allPicked}
                  disabled={open.length === 0 || saving}
                  onChange={flipAll}
                  aria-label={t({ en: 'Select all', ar: 'تحديد الكل' })}
                />
              )}
            </th>
            <th className="tg-idx">#</th>
            <th>{labels.code}</th>
            <th>{labels.item}</th>
            <th className="num">{labels.qty}</th>
            <th>{labels.unit}</th>
            <th className="num">{labels.unitPrice}</th>
            <th className="num">{labels.lineTotal}</th>
            <th>{labels.received}</th>
          </tr>
        </thead>
        <tbody>
          {doc.lines.map((l, i) => {
            const done = !!l.received;
            const on = done || picked.has(l.id);
            return (
              <tr
                key={l.id}
                className={done ? 'is-received' : picked.has(l.id) ? 'is-picked' : undefined}
                onClick={() => { if (canReceive && !done && !saving) flip(l.id); }}
              >
                <td className="tg-check">
                  {(canReceive || done) && (
                    <input
                      type="checkbox"
                      checked={on}
                      disabled={done || !canReceive || saving}
                      onChange={() => flip(l.id)}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={lineName(l, lang)}
                    />
                  )}
                </td>
                <td className="tg-idx">{i + 1}</td>
                <td className="tg-code">{l.code}</td>
                <td>
                  <div className="tg-name">{lineName(l, lang)}</div>
                  {l.notes && <div className="tg-sub">{l.notes}</div>}
                </td>
                <td className="num">{fmt3(l.qty)}</td>
                <td>{lineUnit(l, lang)}</td>
                <td className="num">{fmtMoney(l.sale_price)}</td>
                <td className="num">{fmtMoney(l.sale_total)}</td>
                <td>
                  {done ? (
                    <span className="tg-got">
                      ✓ {labels.allReceived}
                      <span className="tg-sub">
                        {l.received!.implied
                          ? labels.atOnce
                          : [l.received!.by_name ? labels.byWhom(l.received!.by_name) : '', wallClock(l.received!.at)]
                              .filter(Boolean)
                              .join(' · ')}
                      </span>
                    </span>
                  ) : (
                    <span className="tg-wait">{doc.status === 'cancelled' ? labels.cancelled : labels.pending}</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={4}>
              {labels.total} ({fmtInt(doc.lines.length)} {labels.items})
            </td>
            <td className="num">{fmt3(totalQty)}</td>
            <td />
            <td />
            <td className="num">{fmtMoney(totalValue)}</td>
            <td>{fmtInt(receivedCount)} / {fmtInt(doc.lines.length)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// ── Print ──────────────────────────────────────────────────────────────────

function esc(s: string | number | null | undefined): string {
  return String(s ?? '').replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' } as Record<string, string>
  )[c]!);
}

/**
 * The printable body: per document a heading line and its items table, then a
 * grand total. Pure (data in, HTML out) and exported for the test.
 *
 * The item NAME is the first column on purpose: on the 72 mm roll `fitToRoll`
 * makes each row a card headed by its first cell, and "Laban 180ml" is a
 * better card heading than "10890".
 */
export function buildGoodsPrintHtml(
  docs: TransferRowWithLinesDto[],
  l: GoodsLabels,
  lang: string,
  subtitle: string,
): string {
  if (docs.length === 0) return `<div class="rpt-meta">${esc(subtitle)}</div><div class="rpt-empty">${esc(l.empty)}</div>`;
  const parts: string[] = [];
  let grandQty = 0;
  let grandValue = 0;
  let grandLines = 0;
  let grandReceived = 0;
  for (const doc of docs) {
    const lines = doc.lines ?? [];
    const qty = lines.reduce((s, x) => s + x.qty, 0);
    const received = lines.filter((x) => x.received).length;
    const value = lines.reduce((s, x) => s + (x.sale_total ?? 0), 0);
    grandValue += value;
    grandQty += qty;
    grandLines += lines.length;
    grandReceived += received;
    parts.push(
      `<h2 class="section">${esc(l.docNo)} #${esc(doc.entry_no)} · ${esc(wallClock(doc.created_at))}</h2>` +
        `<div class="rpt-meta">${esc(storeName(doc.from_store, lang))} → ${esc(storeName(doc.to_store, lang))}` +
        ` · ${esc(receiptText(doc, l))}${doc.reference_no ? ` · ${esc(l.reference)}: ${esc(doc.reference_no)}` : ''}</div>`,
    );
    parts.push(
      `<table><thead><tr>` +
        `<th>${esc(l.item)}</th><th>${esc(l.code)}</th><th class="num">${esc(l.qty)}</th>` +
        `<th>${esc(l.unit)}</th>` +
        `<th class="num">${esc(l.unitPrice)}</th><th class="num">${esc(l.lineTotal)}</th>` +
        `<th>${esc(l.received)}</th></tr></thead><tbody>` +
        lines
          .map((x) => {
            const got = x.received
              ? `✓ ${x.received.implied ? l.atOnce : [x.received.by_name ?? '', wallClock(x.received.at)].filter(Boolean).join(' ')}`
              : doc.status === 'cancelled' ? l.cancelled : l.pending;
            return (
              `<tr><td>${esc(lineName(x, lang))}</td><td>${esc(x.code)}</td><td class="num">${esc(fmt3(x.qty))}</td>` +
              `<td>${esc(lineUnit(x, lang))}</td>` +
              `<td class="num">${esc(fmtMoney(x.sale_price))}</td><td class="num">${esc(fmtMoney(x.sale_total))}</td>` +
              `<td>${esc(got)}</td></tr>`
            );
          })
          .join('') +
        `</tbody><tfoot><tr><td>${esc(l.total)} (${esc(fmtInt(lines.length))} ${esc(l.items)})</td><td></td>` +
        `<td class="num">${esc(fmt3(qty))}</td><td></td>` +
        `<td></td><td class="num">${esc(fmtMoney(value))}</td>` +
        `<td>${esc(`${received} / ${lines.length}`)}</td></tr></tfoot></table>`,
    );
  }
  parts.push(
    `<h2 class="section">${esc(l.total)}</h2>` +
      `<table><tbody>` +
      `<tr><td>${esc(l.docs)}</td><td class="num">${esc(fmtInt(docs.length))}</td></tr>` +
      `<tr><td>${esc(l.items)}</td><td class="num">${esc(fmtInt(grandLines))}</td></tr>` +
      `<tr><td>${esc(l.totalQty)}</td><td class="num">${esc(fmt3(grandQty))}</td></tr>` +
      `<tr><td>${esc(l.totalValue)}</td><td class="num">${esc(fmtMoney(grandValue))}</td></tr>` +
      `<tr><td>${esc(l.received)}</td><td class="num">${esc(`${grandReceived} / ${grandLines}`)}</td></tr>` +
      `</tbody></table>`,
  );
  return `<div class="rpt-meta">${esc(subtitle)}</div>${parts.join('')}`;
}
